import React, { useEffect, useState } from 'react';
import api from '../../../../config/axios';

import CategoriaFinanceiraModal from './modals/CategoriaFinanceira';
import Paginacao from '../../../../components/utils/paginacao';

import { FaPlus, FaEdit, FaFilter, FaTrashAlt, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const CategoriasFinanceiras = () => {
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    const [categoriasFinanceiras, setCategoriasFinanceiras] = useState([]);
    const [filtroNome, setFiltroNome] = useState('');
    const [filtroTipoContabil, setFiltroTipoContabil] = useState('');
    const [filtroTipoFinanceiro, setFiltroTipoFinanceiro] = useState('');

    const [selectedCategoriaFinanceira, setSelectedCategoriaFinanceira] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const limit = 10;

    const fetchCategoriasFinanceiras = async (currentPage) => {
        try {
            Swal.fire({
                title: 'Carregando...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const params = {
                page: currentPage,
                limit: limit,
                nome: filtroNome,
                tipo_contabil: filtroTipoContabil,
                tipo_financeiro: filtroTipoFinanceiro
            };

            const response = await api.get('/categorias-financeiras', { params });

            setCategoriasFinanceiras(response.data.records || []);
            setTotalPages((Math.ceil(response.data.totalRegistros / limit)) || 1);
            setTotalRegistros(response.data.totalRegistros || 0);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar os dados. Tente novamente mais tarde.');
        } finally {
            Swal.close();
        }
    };

    useEffect(() => {
        fetchCategoriasFinanceiras(currentPage);
    }, [currentPage]);

    const handlePageChange = (currentPage) => {
        setCurrentPage(currentPage);
    };

    const handleAddCategoriaFinanceira = () => {
        setSelectedCategoriaFinanceira(null);
        setIsModalOpen(true);
    };

    const handleEditCategoriaFinanceira = (categoriaFinanceira) => {
        setSelectedCategoriaFinanceira(categoriaFinanceira);
        setIsModalOpen(true);
    };

    const handleDeleteCategoriaFinanceira = async (categoriaFinanceira) => {
        const result = await Swal.fire({
            title: 'Tem certeza?',
            text: `Deseja excluir a categoria "${categoriaFinanceira.nome}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/categorias-financeiras/${categoriaFinanceira.id}`);
                Swal.fire('Excluído!', 'Categoria financeira foi excluída com sucesso.', 'success');
                fetchCategoriasFinanceiras(currentPage);
            } catch (error) {
                Swal.fire('Erro!', error.response?.data?.message || 'Não foi possível excluir a categoria financeira.', 'error');
            }
        }
    };

    const handleFiltrar = () => {
        setCurrentPage(1);
        fetchCategoriasFinanceiras(1);
    };

    const handleLimparFiltros = () => {
        setFiltroNome('');
        setFiltroTipoContabil('');
        setFiltroTipoFinanceiro('');
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedCategoriaFinanceira(null);
    };

    const handleCategoriaFinanceiraAtualizada = () => {
        fetchCategoriasFinanceiras(currentPage);
        handleModalClose();
    };

    const getTipoContabilLabel = (tipo) => {
        const tipos = {
            'receita': 'Receita',
            'despesa': 'Despesa',
            'custo': 'Custo',
            'deducao': 'Dedução'
        };
        return tipos[tipo] || tipo;
    };

    const getTipoFinanceiroLabel = (tipo) => {
        const tipos = {
            'receita': 'Receita',
            'despesa': 'Despesa'
        };
        return tipos[tipo] || tipo;
    };

    const getTipoVinculoLabel = (tipo) => {
        const tipos = {
            'gestora': 'Gestora',
            'unidade': 'Unidade'
        };
        return tipos[tipo] || tipo;
    };

    return (
        <div className="container-fluid">
            <div className="card">
                <div className="card-body">
                    <div className="row mt-3">
                        <div className="col-12">
                            <div className="d-flex align-items-center">
                                <button
                                    className="btn btn-outline-secondary me-3"
                                    onClick={() => navigate('/financeiro/cadastros')}
                                    title="Voltar para Cadastros"
                                >
                                    <FaArrowLeft />
                                </button>
                                <h4 className="fw-bold mb-0">Categorias Financeiras</h4>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-3 mb-3">
                        <div className="col-md-6 col-12">
                            <button className="btn btn-outline-primary" onClick={handleAddCategoriaFinanceira}>
                                <FaPlus /> Adicionar Categoria Financeira
                            </button>
                        </div>
                    </div>

                    <div className="row mt-4 mb-4">
                        <div className="col-lg-3 col-12">
                            <label className="form-label fw-bold">Nome</label>
                            <input
                                type="text"
                                autoComplete="off"
                                placeholder="Digite o nome da categoria"
                                value={filtroNome}
                                className="form-control"
                                onChange={(e) => setFiltroNome(e.target.value.toUpperCase())}
                            />
                        </div>
                        <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                            <label className="form-label fw-bold">Tipo Contábil</label>
                            <select
                                value={filtroTipoContabil}
                                className="form-control"
                                onChange={(e) => setFiltroTipoContabil(e.target.value)}
                            >
                                <option value="">Todos os tipos</option>
                                <option value="receita">Receita</option>
                                <option value="despesa">Despesa</option>
                                <option value="custo">Custo</option>
                                <option value="deducao">Dedução</option>
                            </select>
                        </div>
                        <div className="col-lg-2 col-12 mt-lg-0 mt-3">
                            <label className="form-label fw-bold">Tipo Financeiro</label>
                            <select
                                value={filtroTipoFinanceiro}
                                className="form-control"
                                onChange={(e) => setFiltroTipoFinanceiro(e.target.value)}
                            >
                                <option value="">Todos</option>
                                <option value="receita">Receita</option>
                                <option value="despesa">Despesa</option>
                            </select>
                        </div>
                        <div className="col-lg-4 col-12 mt-lg-0 mt-3 d-flex align-items-end">
                            <div className="d-flex gap-2 w-100 justify-content-end">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={handleLimparFiltros}
                                >
                                    Limpar
                                </button>
                                <button
                                    className="btn btn-custom-primary"
                                    onClick={handleFiltrar}
                                >
                                    <FaFilter /> Filtrar
                                </button>
                            </div>
                        </div>
                    </div>

                    {error ? (
                        <p className="error-message">{error}</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead className="table-custom-primary">
                                    <tr>
                                        <th className="text-nowrap">Nome</th>
                                        <th className="text-nowrap">Tipo Financeiro</th>
                                        <th className="text-nowrap">Tipo Contábil</th>
                                        <th className="text-nowrap">Vínculo</th>
                                        <th className="text-nowrap">Código Contábil</th>
                                        <th className="text-nowrap">Código Reduzido</th>
                                        <th className="text-center">Ativo</th>
                                        <th className="text-center">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categoriasFinanceiras.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="text-center">
                                                Nenhuma categoria financeira encontrada.
                                            </td>
                                        </tr>
                                    ) : (
                                        categoriasFinanceiras.map((categoriaFinanceira, index) => (
                                            <tr key={index}>
                                                <td className="text-nowrap">{categoriaFinanceira.nome}</td>
                                                <td className="text-nowrap">
                                                    <span className={`badge ${categoriaFinanceira.tipo_financeiro === 'receita' ? 'bg-success' : 'bg-danger'
                                                        }`}>
                                                        {getTipoFinanceiroLabel(categoriaFinanceira.tipo_financeiro)}
                                                    </span>
                                                </td>
                                                <td className="text-nowrap">
                                                    <span className={`badge ${categoriaFinanceira.tipo_contabil === 'receita' ? 'bg-success' :
                                                            categoriaFinanceira.tipo_contabil === 'despesa' ? 'bg-danger' :
                                                                categoriaFinanceira.tipo_contabil === 'custo' ? 'bg-warning' : 'bg-info'
                                                        }`}>
                                                        {getTipoContabilLabel(categoriaFinanceira.tipo_contabil)}
                                                    </span>
                                                </td>
                                                <td className="text-nowrap">
                                                    <span className={`badge ${categoriaFinanceira.tipo_vinculo === 'gestora' ? 'bg-primary' : 'bg-info'}`}>
                                                        {getTipoVinculoLabel(categoriaFinanceira.tipo_vinculo)}
                                                    </span>
                                                </td>
                                                <td>{categoriaFinanceira.codigo_contabil || '-'}</td>
                                                <td>{categoriaFinanceira.codigo_reduzido || '-'}</td>
                                                <td className="text-center">
                                                    {categoriaFinanceira.ativo ? (
                                                        <span className="fw-bold">Sim</span>
                                                    ) : (
                                                        <span className="fw-bold">Não</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <button
                                                            className="btn btn-custom-secondary btn-sm"
                                                            onClick={() => handleEditCategoriaFinanceira(categoriaFinanceira)}
                                                            title="Editar"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className="btn btn-custom-secondary btn-sm ms-3"
                                                            onClick={() => handleDeleteCategoriaFinanceira(categoriaFinanceira)}
                                                            title="Excluir"
                                                        >
                                                            <FaTrashAlt />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="mb-3">
                            <span>
                                Total de registros: <strong>{totalRegistros}</strong>
                            </span>
                        </div>
                        <Paginacao
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <CategoriaFinanceiraModal
                    categoriaFinanceira={selectedCategoriaFinanceira}
                    onClose={handleModalClose}
                    onCategoriaFinanceiraAtualizada={handleCategoriaFinanceiraAtualizada}
                />
            )}
        </div>
    );
};

export default CategoriasFinanceiras; 